import React, { useEffect, useState } from "react";

// Self-contained default styling so this component doesn't depend on any
// importing page defining its own ".input" CSS class or INPUT_CLASS constant —
// drop this file into any page and it looks right with zero extra setup.
const DEFAULT_BUTTON_CLASS =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-950 focus:ring-2 focus:ring-slate-950/10 flex items-center justify-between text-left";

/**
 * Shared date/time picker used across sailing and booking forms.
 *
 * IMPORTANT: this is the single source of truth for this component. It used
 * to be copy-pasted separately into EditSailing.js, BookingRequestPage.jsx,
 * and NewSailing.js — which is exactly how a bug (time-only selection
 * silently defaulting to the 1st of the viewed month instead of today) got
 * fixed in one copy but stayed broken in the others. Import this instead of
 * pasting a new copy, so a fix here fixes it everywhere at once.
 *
 * @param {string} value - datetime-local formatted string, e.g. "2026-08-02T14:30"
 * @param {(val: string) => void} onChange - called with the new datetime-local string (or "" on Clear)
 * @param {string} [placeholder]
 * @param {string} [buttonClassName] - override the trigger button's className if the default doesn't fit
 */
export default function DateTimePicker({ value, onChange, placeholder = "Select date & time", buttonClassName }) {
  const [open, setOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    const d = value ? new Date(value) : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const wrapRef = React.useRef(null);

  const selected = value ? new Date(value) : null;
  const hh = selected ? String(selected.getHours()).padStart(2, "0") : "12";
  const mm = selected ? String(selected.getMinutes()).padStart(2, "0") : "00";

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (open) {
      const d = value ? new Date(value) : new Date();
      setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, [open, value]);

  const toLocalString = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const pickDay = (day) => {
    const base = selected ? new Date(selected) : new Date();
    base.setFullYear(viewMonth.getFullYear(), viewMonth.getMonth(), day);
    if (!selected) { base.setHours(12, 0, 0, 0); }
    onChange(toLocalString(base));
  };

  // Defaults to TODAY (not day 1 of the viewed month) when no day has been
  // picked yet — matches pickDay's own fallback. (This is the fix that used
  // to be missing from some copies of this component.)
  const setTime = (newHH, newMM) => {
    const base = selected ? new Date(selected) : new Date();
    base.setHours(Number(newHH), Number(newMM), 0, 0);
    onChange(toLocalString(base));
  };

  const monthLabel = viewMonth.toLocaleDateString("en-NZ", { month: "long", year: "numeric" });
  const firstDayOfWeek = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1).getDay();
  const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
  const today = new Date();
  const isToday = (day) => today.getFullYear() === viewMonth.getFullYear() && today.getMonth() === viewMonth.getMonth() && today.getDate() === day;
  const isSelected = (day) => selected && selected.getFullYear() === viewMonth.getFullYear() && selected.getMonth() === viewMonth.getMonth() && selected.getDate() === day;

  const displayValue = selected
    ? selected.toLocaleString("en-NZ", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => {
          if (!open && wrapRef.current) {
            // Popup is roughly 420px tall at most (month nav + weekday row +
            // up to 6 day-rows + time row + padding). If there isn't enough
            // room below the field in the viewport, open upward instead —
            // otherwise the time row at the bottom can end up rendered below
            // the visible browser window, looking like it's "cut off".
            const rect = wrapRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            setOpenUpward(spaceBelow < 420 && spaceAbove > spaceBelow);
          }
          setOpen((o) => !o);
        }}
        className={buttonClassName || DEFAULT_BUTTON_CLASS}
      >
        <span className={displayValue ? "text-slate-900" : "text-slate-400"}>
          {displayValue || placeholder}
        </span>
        <svg className="h-4 w-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>

      {open && (
        <div className={`absolute z-50 w-72 rounded-2xl border border-slate-200 bg-white p-4 shadow-lg ${openUpward ? "bottom-full mb-2" : "top-full mt-2"}`}>
          <div className="mb-3 flex items-center justify-between">
            <button type="button"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            </button>
            <span className="text-sm font-semibold text-slate-900">{monthLabel}</span>
            <button type="button"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>

          <div className="mb-1 grid grid-cols-7 text-center text-[11px] font-medium text-slate-400">
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
          </div>

          <div className="grid grid-cols-7 gap-y-1 text-center text-sm">
            {Array.from({ length: firstDayOfWeek }).map((_, i) => <div key={`pad-${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => pickDay(day)}
                  className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full transition ${
                    isSelected(day)
                      ? "bg-slate-900 font-semibold text-white"
                      : isToday(day)
                      ? "border border-slate-300 text-slate-900"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-3">
            <span className="text-xs font-medium text-slate-500">Time</span>
            <select
              value={hh}
              onChange={(e) => setTime(e.target.value, mm)}
              className="rounded-lg border border-slate-200 px-2 py-1 text-sm text-slate-900 outline-none focus:border-slate-400"
            >
              {Array.from({ length: 24 }).map((_, h) => (
                <option key={h} value={String(h).padStart(2, "0")}>{String(h).padStart(2, "0")}</option>
              ))}
            </select>
            <span className="text-slate-400">:</span>
            <select
              value={mm}
              onChange={(e) => setTime(hh, e.target.value)}
              className="rounded-lg border border-slate-200 px-2 py-1 text-sm text-slate-900 outline-none focus:border-slate-400"
            >
              {["00", "15", "30", "45"].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="ml-auto rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700"
            >
              Done
            </button>
          </div>

          {value && (
            <button
              type="button"
              onClick={() => { onChange(""); setOpen(false); }}
              className="mt-2 w-full text-center text-xs font-medium text-slate-400 hover:text-red-500"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}